/* eslint-disable react/prop-types */
import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { isObject, noop, get } from 'lodash';
import { useDataStore } from './DataStore';

const SelectionContext = createContext();
const SelectionDispatchContext = createContext();

export const SELECTIONMODE_NONE = 'none';
export const SELECTIONMODE_SINGLE = 'single';
export const SELECTIONMODE_MULTI = 'multi';

const ACTION_RESET_OPTIONS = 'reset';
const ACTION_SET_SELECTION_MODEL = 'setSelectionModel';
const ACTION_CLEAR = 'clear';
const ACTION_SELECT = 'select';
const ACTION_DESELECT = 'deselect';
const ACTION_SELECT_ALL = 'selectAll';
const ACTION_DESELECT_ALL = 'deselectAll';
const ACTION_SET_TOTAL_ROWS = 'setTotalRows';

const SELECTION_ACTIONS = {
    [ACTION_RESET_OPTIONS]: resetOptions,
    [ACTION_SET_SELECTION_MODEL]: setSelectionModel,
    [ACTION_CLEAR]: clearSelection,
    [ACTION_SELECT]: select,
    [ACTION_DESELECT]: deselect,
    [ACTION_SELECT_ALL]: selectAll,
    [ACTION_DESELECT_ALL]: deselectAll,
    [ACTION_SET_TOTAL_ROWS]: setTotalRows,
};

export default function SelectionProvider(props) {
    const { children, options, selection, canSelectCondition } = props;
    const { rows: tableRows } = useDataStore();
    const [state, dispatch] = useReducer(selectionReducer, initSelectionState(options));
    const { selectionLength, filteredTotalRows } = state ? getSelectionLength(state, tableRows, canSelectCondition) : 0;

    useSelectionEffects(state, dispatch, options, selection, tableRows, selectionLength);

    const selectionState = useMemo(
        () => ({
            mode: state.mode,
            totalRows: state.totalRows,
            needSingleAutoSelectFirst: state.needSingleAutoSelectFirst,

            enabled: state.mode === SELECTIONMODE_MULTI || state.mode === SELECTIONMODE_SINGLE,

            hasSelection: selectionLength > 0,
            allSelected: selectionLength === filteredTotalRows,
            selectionLength,
            allowSelectAll: state.allowSelectAll,
            isSelected: (rowOrId) => isSelected(rowOrId, state),
        }),
        [selectionLength, state, filteredTotalRows]
    );

    const selectionActions = useMemo(
        () => ({
            select: (id) => dispatch({ type: ACTION_SELECT, params: { id, canSelectCondition } }),
            deselect: (id) => dispatch({ type: ACTION_DESELECT, params: { id } }),
            selectAll: (rows) => dispatch({ type: ACTION_SELECT_ALL, params: { rows, canSelectCondition } }),
            deselectAll: () => dispatch({ type: ACTION_DESELECT_ALL }),
            clear: () => dispatch({ type: ACTION_CLEAR }),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <SelectionContext.Provider value={selectionState}>
            <SelectionDispatchContext.Provider value={selectionActions}>{children}</SelectionDispatchContext.Provider>
        </SelectionContext.Provider>
    );
}

export function useSelection() {
    return useContext(SelectionContext);
}

export function useSelectionActions() {
    return useContext(SelectionDispatchContext);
}

function useSelectionEffects(state, dispatch, options, selection, tableRows, selectionLength) {
    const {
        mode = SELECTIONMODE_NONE,
        needSingleAutoSelectFirst = false,
        onChange,
        allowSelectAll = false,
        allowReselect = false,
        force = false,
        idMapper = null,
        idMapping = 'id',
        totalRows = 0,
    } = options;

    const callbackRef = useRef();
    callbackRef.current = onChange || noop;

    // Use a ref for tableRows and selectionLength, so the onChange callback always receives current rows and selectionLength
    // without triggering the effect when table data loads/changes.
    // Only selection model changes should invoke the onChange callback.
    const tableRowsRef = useRef(tableRows);
    tableRowsRef.current = tableRows;

    const selectionLengthRef = useRef(selectionLength);
    selectionLengthRef.current = selectionLength;

    // reset options when one of them changes
    useEffect(() => {
        const newOptions = {
            mode,
            needSingleAutoSelectFirst,
            onChange: callbackRef.current,
            allowSelectAll,
            allowReselect,
            force,
            idMapper,
            idMapping,
            totalRows,
            selectionLength,
        };

        dispatch({ type: ACTION_RESET_OPTIONS, params: { options: newOptions } });
    }, [
        dispatch,
        mode,
        needSingleAutoSelectFirst,
        allowSelectAll,
        allowReselect,
        force,
        idMapper,
        idMapping,
        totalRows,
        selectionLength,
    ]);

    // apply a new selection if/when that happens externally
    useEffect(() => {
        dispatch({ type: ACTION_SET_SELECTION_MODEL, params: { model: selection } });
    }, [dispatch, selection]);

    const model = state?.model;
    useEffect(() => {
        if (model) {
            callbackRef.current(copySelectionModel(model), tableRowsRef.current, selectionLengthRef.current);
        }
    }, [model]);
}

function initSelectionState(options, model) {
    const { mode, needSingleAutoSelectFirst, allowSelectAll, allowReselect, force, idMapper, idMapping, totalRows } =
        options;

    // if we do not support select all, do not let inverted selections to be set
    let newModel = model;
    if (!allowSelectAll && model?.inverted) {
        newModel = null;
    }

    if (mode === SELECTIONMODE_SINGLE && model?.selection?.length > 1) {
        newModel = null;
    }

    return {
        mode,
        needSingleAutoSelectFirst,
        allowSelectAll,
        allowReselect,
        force,
        idMapper,
        idMapping,
        totalRows,

        model: newModel || {
            inverted: false,
            selection: [],
        },
    };
}

function copySelectionModel(model) {
    const { selection, inverted } = model;

    // return a copy of the selection model
    return { selection: [...selection], inverted };
}

function selectionReducer(state, action) {
    const { type, params } = action;

    return SELECTION_ACTIONS[type](state, params);
}

function resetOptions(state, { options }) {
    return initSelectionState(options, state?.model);
}

function setSelectionModel(state, { model }) {
    const { mode } = state;

    let selection = model?.selection || [];

    if (mode === SELECTIONMODE_SINGLE && selection.length > 1) {
        const [firstItem] = selection;
        selection = firstItem ? [firstItem] : [];
    }

    return {
        ...state,

        model: {
            inverted: model?.inverted || false,
            selection,
        },
    };
}

function setTotalRows(state, { totalRows }) {
    return { ...state, totalRows };
}

function clearSelection(state) {
    return {
        ...state,

        model: {
            inverted: false,
            selection: [],
        },
    };
}

function select(state, { id: rowOrId, canSelectCondition }) {
    const id = toId(rowOrId, state);

    if (canSelectCondition) {
        if (isObject(rowOrId) && !canSelectCondition(rowOrId)) {
            return state;
        }
    }

    if (!state.allowReselect && (isSelected(id, state) || state.mode === SELECTIONMODE_NONE)) {
        return state;
    }

    const { mode, model } = state;

    const { selection, inverted } = model;

    let newSelection;

    if (!inverted) {
        newSelection = mode === SELECTIONMODE_SINGLE ? [id] : [...selection, id];
    } else {
        // if inverted, we remove the item from the array
        const idx = selection.indexOf(id);
        newSelection = [...selection.slice(0, idx), ...selection.slice(idx + 1)];
    }

    return { ...state, model: { inverted, selection: newSelection } };
}

function deselect(state, { id: rowOrId }) {
    if (state.allowReselect) {
        // Deselect should be treated as a "reselect"
        return select(state, { id: rowOrId });
    }

    const id = toId(rowOrId, state);

    const {
        force,
        totalRows,
        model: { selection: initialSelection },
    } = state;
    let {
        model: { inverted },
    } = state;

    if (!isSelected(id, state) || state.mode === SELECTIONMODE_NONE || (initialSelection.length === 1 && force)) {
        return state;
    }

    let newSelection;

    if (!inverted) {
        const idx = initialSelection.indexOf(id);
        newSelection = [...initialSelection.slice(0, idx), ...initialSelection.slice(idx + 1)];
    } else {
        // if inverted, adding an item to the selection inverts it
        newSelection = [...initialSelection, id];

        // If we're inverted and all rows are selected, we are back at our initial no selection state,
        // so update the selection state to reflect that so that UI state reliant on whether or no there
        // are selections will render properly
        if (newSelection.length === totalRows) {
            newSelection = [];
            inverted = false;
        }
    }

    return { ...state, model: { inverted, selection: newSelection } };
}

function selectAll(state, { rows, canSelectCondition }) {
    if (state.mode !== SELECTIONMODE_MULTI) {
        throw new Error('Select all requires multiple selections');
    }

    let selection = [];
    if (canSelectCondition) {
        const dataRows = Object.values(rows).map((row) => row.row);
        selection = dataRows.filter((row) => !canSelectCondition(row)).map((row) => row._id);
    }

    return {
        ...state,
        model: {
            inverted: true,
            selection,
        },
    };
}

function deselectAll(state) {
    if (state.mode !== SELECTIONMODE_MULTI) {
        throw new Error('Deselect all requires multiple selections');
    }

    return {
        ...state,

        model: {
            inverted: false,
            selection: [],
        },
    };
}

function isSelected(rowOrId, state) {
    const id = toId(rowOrId, state);

    const {
        allowSelectAll,
        mode,
        model: { selection, inverted },
    } = state;

    if (mode === SELECTIONMODE_NONE) {
        return false;
    }

    const isInSelection = selection.indexOf(id) > -1;
    return allowSelectAll && inverted ? !isInSelection : isInSelection;
}

function getSelectionLength(state, rows, canSelectCondition) {
    const { allowSelectAll, totalRows, model } = state;

    if (!model) {
        return 0;
    }

    const { inverted, selection } = model;
    const len = selection.length;

    let filteredTotalRows = totalRows;
    if (canSelectCondition) {
        filteredTotalRows = Object.values(rows)
            .map((row) => row.row)
            .filter((row) => canSelectCondition(row))
            .map((row) => row).length;
    }

    return { selectionLength: allowSelectAll && inverted ? totalRows - len : len, filteredTotalRows };
}

function toId(rowOrId, state) {
    const { idMapper, idMapping } = state;

    if (typeof rowOrId === 'object') {
        return typeof idMapper !== 'function' ? get(rowOrId, idMapping) : idMapper(rowOrId);
    }

    return rowOrId;
}
